import React, { useState, useEffect, createContext, useContext } from 'react';
import { supabase } from '../../lib/supabase';
import { RobotsConfig } from '../../components/admin/RobotsTxtConfigSection';
import { SeoConfig } from '../../components/admin/SeoConfigSection'; // Import SeoConfig
import { SystemConfig } from '../../components/admin/SystemConfigSection'; // Import SystemConfig
import { AIPrompts } from '../../components/admin/AiPromptsSection'; // Import AIPrompts

// Types pour les paramètres
interface Settings {
  systemConfig: SystemConfig;
  aiPrompts: AIPrompts;
  seoConfig: SeoConfig;
  robotsConfig: RobotsConfig;
  // Retaining testMode at the top level for now, though it's also in seoConfig
  // Consider consolidating if testMode in seoConfig is the primary one.
  testMode: boolean; 
  noIndexEnabled: boolean; // This seems redundant if seoConfig.metaNoIndex is used. Consider removing.
}

// Type pour le contexte
interface SettingsContextType {
  settings: Settings;
  updateSettings: (newSettings: Partial<Settings>) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

// Valeurs par défaut
const defaultSettings: Settings = {
  systemConfig: {
    domain: 'https://app.smart-impact.ch',
    invalidTokenRedirect: 'https://smart-impact.ch',
    tokenPrefix: '/t/'
  },
  aiPrompts: {
    companySummary: 'Analyze the following company data and create a concise summary...',
    recommendations: 'Based on the digital presence analysis, recommend improvements...',
    digitalScore: 'Calculate a digital score based on the following metrics...'
  },
  seoConfig: {
    testMode: false,
    metaNoIndex: false,
    defaultTitle: 'Cold Sales System',
    defaultDescription: 'Optimisez votre prospection commerciale avec Cold Sales System.',
    defaultKeywords: 'cold sales, prospection, marketing, b2b',
    ogSiteName: 'Cold Sales System',
    ogImage: '', 
    twitterSite: '',
    sitemapRoutes: '/, /login, /register, /features, /pricing, /contact',
  },
  robotsConfig: {
    content: `User-agent: *\nDisallow: /mon-espace/\nAllow: /\n\nSitemap: https://example.com/sitemap.xml`,
    active: true
  },
  testMode: false, // Top-level, potentially redundant
  noIndexEnabled: true // Top-level, potentially redundant
};

// Création du contexte
const SettingsContext = createContext<SettingsContextType>({
  settings: defaultSettings,
  updateSettings: async () => {},
  isLoading: false,
  error: null
});

// Provider du contexte
export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<Settings>(defaultSettings);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Charger les paramètres depuis localStorage ou la base de données
  useEffect(() => {
    const loadSettings = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // D'abord essayer de charger depuis localStorage
        const storedSettings = localStorage.getItem('appSettings');
        if (storedSettings) {
          setSettings(JSON.parse(storedSettings));
          setIsLoading(false);
          return;
        }
        
        // Sinon, charger depuis Supabase si l'utilisateur est connecté
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          // Charger tous les paramètres
          const { data, error } = await supabase
            .from('settings')
            .select('*');
            
          if (error) {
            throw error;
          }
          
          if (data && data.length > 0) {
            // Convertir les données de format clé-valeur en objet structuré
            const settingsMap: Record<string, any> = {};
            data.forEach((item: { key: string; value: any }) => {
              settingsMap[item.key] = item.value;
            });
            
            const newSettings: Settings = {
              systemConfig: settingsMap.systemConfig || defaultSettings.systemConfig,
              aiPrompts: settingsMap.aiPrompts || defaultSettings.aiPrompts,
              seoConfig: settingsMap.seoConfig || defaultSettings.seoConfig,
              robotsConfig: settingsMap.robotsConfig || defaultSettings.robotsConfig,
              testMode: settingsMap.testMode !== undefined ? settingsMap.testMode : defaultSettings.testMode,
              noIndexEnabled: settingsMap.noIndexEnabled !== undefined ? settingsMap.noIndexEnabled : defaultSettings.noIndexEnabled,
            };
            
            setSettings(newSettings);
            localStorage.setItem('appSettings', JSON.stringify(newSettings));
          }
        }
      } catch (err) {
        setError(`Erreur lors du chargement des paramètres: ${err instanceof Error ? err.message : 'Erreur inconnue'}`);
        console.error('Erreur de chargement des paramètres:', err);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadSettings();
  }, []);
  
  // Mettre à jour les paramètres
  const updateSettings = async (newSettings: Partial<Settings>) => {
    try {
      const updatedSettings = { ...settings, ...newSettings };
      
      // Sauvegarder dans localStorage
      localStorage.setItem('appSettings', JSON.stringify(updatedSettings));
      setSettings(updatedSettings);
      
      // Sauvegarder dans Supabase si l'utilisateur est connecté
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        // Préparer les données à mettre à jour dans le format clé-valeur
        const settingsToUpdate = [
          { key: 'systemConfig', value: updatedSettings.systemConfig },
          { key: 'aiPrompts', value: updatedSettings.aiPrompts },
          { key: 'seoConfig', value: updatedSettings.seoConfig },
          { key: 'robotsConfig', value: updatedSettings.robotsConfig },
          { key: 'testMode', value: updatedSettings.testMode },
          { key: 'noIndexEnabled', value: updatedSettings.noIndexEnabled }
        ];
        
        // Mettre à jour chaque paramètre individuellement
        for (const setting of settingsToUpdate) {
          const { error } = await supabase
            .from('settings')
            .upsert({
              key: setting.key,
              value: setting.value,
              updated_at: new Date().toISOString()
            });
            
          if (error) {
            throw error;
          }
        }
      }
    } catch (err) {
      setError(`Erreur lors de la mise à jour des paramètres: ${err instanceof Error ? err.message : 'Erreur inconnue'}`);
      console.error('Erreur de mise à jour des paramètres:', err);
    }
  };
  
  return (
    <SettingsContext.Provider value={{ settings, updateSettings, isLoading, error }}>
      {children}
    </SettingsContext.Provider>
  );
};

// Hook personnalisé pour utiliser le contexte
export const useSettings = () => useContext(SettingsContext);
